import { useEffect, useRef } from 'react'
import Chart from 'chart.js'
import { Card, Col, Empty, Row, Spin, Typography } from 'antd'
import PropTypes from 'prop-types'
import './ReportWordLangPieCharts.scss'

const { Text } = Typography

const LABELS = ['Missing definition', 'Definition added']

const PIE_COLORS = ['#eb2f96', '#52c41a']

/** Smallest slice share of the pie for any non-zero count so slivers stay visible (~3.5% ≈ 12°). */
const MIN_NONZERO_SLICE_SHARE = 0.035

/**
 * Chart.js draws near-zero arcs as invisible. Scale small non-zero segments up for display only;
 * tooltips still use actual counts.
 */
function pieDisplayValues(miss, add) {
	const actual = [Number(miss) || 0, Number(add) || 0]
	const total = actual[0] + actual[1]
	if (total <= 0) {
		return { display: actual, actual }
	}
	const minVis = total * MIN_NONZERO_SLICE_SHARE
	const display = actual.map(v => {
		if (v <= 0) return 0
		return v < minVis ? minVis : v
	})
	return { display, actual }
}

/** Draws real counts on each slice (Chart.js 2 has no built-in slice labels). */
function pieSliceValueLabelsPlugin(actualCounts) {
	return {
		afterDatasetsDraw(chart) {
			const ctx = chart.ctx
			const meta = chart.getDatasetMeta(0)
			if (!meta || !meta.data || !meta.data.length) return

			ctx.save()
			ctx.font =
				'600 15px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
			ctx.textAlign = 'center'
			ctx.textBaseline = 'middle'

			meta.data.forEach((arc, index) => {
				const value = actualCounts[index]
				if (value == null || value === 0) return
				const pos = arc.tooltipPosition()
				ctx.fillStyle = 'rgba(255, 255, 255, 0.96)'
				ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
				ctx.shadowBlur = 8
				ctx.shadowOffsetX = 0
				ctx.shadowOffsetY = 1
				ctx.fillText(String(value), pos.x, pos.y)
				ctx.shadowBlur = 0
			})
			ctx.restore()
		},
	}
}

function createPie(canvas, miss, add) {
	if (!canvas) return null
	const { display, actual } = pieDisplayValues(miss, add)
	const ctx = canvas.getContext('2d')
	return new Chart(ctx, {
		type: 'pie',
		plugins: [pieSliceValueLabelsPlugin(actual)],
		data: {
			labels: LABELS,
			datasets: [
				{
					data: display,
					backgroundColor: PIE_COLORS,
					borderWidth: 2,
					borderColor: '#fff',
				},
			],
		},
		options: {
			maintainAspectRatio: false,
			legend: {
				position: 'bottom',
			},
			tooltips: {
				callbacks: {
					label(tooltipItem, data) {
						const i = tooltipItem.index
						const v = actual[i]
						const l = data.labels[i]
						return `${l}: ${v}`
					},
				},
			},
		},
	})
}

function LangBlock({ title, bucket, canvasRef }) {
	const miss = bucket?.missingDefinition ?? 0
	const add = bucket?.definitionAdded ?? 0
	const hasData = miss > 0 || add > 0

	return (
		<Card size="small" bordered className="report-lang-pie__card">
			<div className="report-lang-pie__title">{title}</div>
			<Text type="secondary" className="report-lang-pie__counts">
				Missing definition: {miss} · Definition added: {add}
			</Text>
			<div className="report-lang-pie__canvas-wrap">
				{hasData ? (
					<canvas ref={canvasRef} className="report-lang-pie__canvas" />
				) : (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description="No data for this language"
						className="report-lang-pie__empty"
					/>
				)}
			</div>
		</Card>
	)
}

LangBlock.propTypes = {
	title: PropTypes.string.isRequired,
	bucket: PropTypes.shape({
		missingDefinition: PropTypes.number,
		definitionAdded: PropTypes.number,
	}),
	canvasRef: PropTypes.object.isRequired,
}

export const ReportWordLangPieCharts = ({ stats, loading, englishOnly }) => {
	const enCanvasRef = useRef(null)
	const viCanvasRef = useRef(null)
	const enChartRef = useRef(null)
	const viChartRef = useRef(null)

	useEffect(() => {
		const destroy = () => {
			if (enChartRef.current) {
				enChartRef.current.destroy()
				enChartRef.current = null
			}
			if (viChartRef.current) {
				viChartRef.current.destroy()
				viChartRef.current = null
			}
		}

		destroy()

		if (loading || !stats) {
			return destroy
		}

		const enMiss = stats.english?.missingDefinition ?? 0
		const enAdd = stats.english?.definitionAdded ?? 0
		const viMiss = stats.vietnamese?.missingDefinition ?? 0
		const viAdd = stats.vietnamese?.definitionAdded ?? 0

		if (enMiss > 0 || enAdd > 0) {
			enChartRef.current = createPie(enCanvasRef.current, enMiss, enAdd)
		}
		if (!englishOnly && (viMiss > 0 || viAdd > 0)) {
			viChartRef.current = createPie(viCanvasRef.current, viMiss, viAdd)
		}

		return destroy
	}, [stats, loading, englishOnly])

	if (loading) {
		return (
			<div className="report-lang-pie report-lang-pie--loading">
				<Spin tip="Loading chart data…" />
			</div>
		)
	}

	return (
		<Row gutter={[16, 16]} className="report-lang-pie">
			<Col xs={24} md={englishOnly ? 24 : 12}>
				<LangBlock
					title="Word column language: English (en)"
					bucket={stats?.english}
					canvasRef={enCanvasRef}
				/>
			</Col>
			{!englishOnly ? (
				<Col xs={24} md={12}>
					<LangBlock
						title="Word column language: Vietnamese (vi)"
						bucket={stats?.vietnamese}
						canvasRef={viCanvasRef}
					/>
				</Col>
			) : null}
		</Row>
	)
}

ReportWordLangPieCharts.propTypes = {
	stats: PropTypes.shape({
		english: PropTypes.object,
		vietnamese: PropTypes.object,
	}),
	loading: PropTypes.bool,
	englishOnly: PropTypes.bool,
}

ReportWordLangPieCharts.defaultProps = {
	stats: null,
	loading: false,
	englishOnly: false,
}
